<script>
/* global Paddle */
import { computed } from '@vue/composition-api';
import { mapState, mapActions } from 'pinia';
import {
  useAuthStore, useDeploymentStore, useCompanyStore, useServerCreationStore,
} from '@/stores';
import { AnalyticsMixin } from '@/utils/mixins/analytics';
import { GtmEvent, pushGtmEvent } from '@/utils/gtm';
import { IntercomMixin } from './utils/mixins/intercom';
import GenericPopups from './views/fragments/GenericPopups.vue';
import WelcomeLayout from './components/layouts/welcome-layout';
import DashboardLayout from './components/layouts/dashboard-layout';
import BaseLayout from './components/layouts/base-layout';
import TopProgressBar from './components/common/top-progress-bar.vue';
import EnvironmentBanner from './components/common/environment-banner.vue';
import * as DeploymentService from './modules/deployment/DeploymentService';
import { getLastRedirected } from './router/index';
import { countlyVariables } from './constants';
import { deleteCookie, getCookie } from './utils/cookie';
import PaymentModal from './views/dashboard/deployments/containers/payment-modal.vue';
import * as AuthService from './modules/user/AuthService';
import { ResponseMessage } from '../../../packages/commons/src/enums/ResponseMessage';

const routesRequiredConfigurables = ['deploymentsRoot', 'deploymentsDetail', 'dashboardRoot', 'allServers', 'adminServerUserView'];

export default {
  components: {
    'generic-popups': GenericPopups,
    PaymentModal,
    TopProgressBar,
    EnvironmentBanner,
  },
  mixins: [AnalyticsMixin, IntercomMixin],
  setup() {
    const authStore = useAuthStore();
    const isLoggedIn = computed(() => authStore.isLoggedIn);

    // verify authentication when user focus to browser
    const {
      error: authenticationError,
    } = AuthService.swr.meSwr(isLoggedIn);

    return {
      authenticationError,
    };
  },
  data() {
    return {
      configurables: {
        'mock-cloud': {},
        gcp: {},
      },
      plans: [],
      isServerCreationPopupOpen: true,
    };
  },
  computed: {
    ...mapState(useAuthStore, ['loggedInUser', 'loggedInUserError', 'isLoggedIn', 'loggedInUserState']),
    ...mapState(useCompanyStore, ['selectedCompany']),
    ...mapState(useDeploymentStore, ['showPaymentModal']),
    currentLayout() {
      if (this.$route.meta.welcome) {
        return WelcomeLayout;
      }
      if (this.$route.meta.prelogin) {
        return BaseLayout;
      }
      return DashboardLayout;
    },
    isIntercomVisible() {
      return this.$route.name !== 'Loading' && !this.$route.meta.onboarding && this.isLoggedIn;
    },
  },
  watch: {
    loggedInUserState(newVal) {
      if (newVal === 'IN_RESUMED' && this.$route.meta.prelogin) {
        this.$router.push(getLastRedirected() || '/profile/account');
      }
    },
    async authenticationError() {
      if (this.authenticationError.message === ResponseMessage.AUTHENTICATION_FAILED && this.isLoggedIn) {
        await this.terminateSession();
      }
    },
    '$route.name': async function (routeName) {
      if (routesRequiredConfigurables.indexOf(routeName) > -1) {
        await this.loadConfigurables();
      }

      if (this.isIntercomVisible) {
        await this.startIntercom();
      }
    },
  },
  async mounted() {
    const IS_PROD = window.location.hostname === 'flex.countly.com';

    Paddle.Initialize({
      token: IS_PROD ? countlyVariables.PADDLE_CLIENT_TOKEN_PROD : countlyVariables.PADDLE_CLIENT_TOKEN_DEV,
      eventCallback: (data) => {
        if (data.name === 'checkout.completed') {
          this.pushGtmSubscriptionComplete(data);

          const targetRoute = {
            name: 'serverCreated',
          };
          if (this.$route.params.id) {
            targetRoute.params = {
              id: this.$route.params.id,
            };
          }

          this.$router.push(targetRoute);
        }

        if (data.name === 'checkout.discount.applied') {
          this.setServerCreationState(['pricing', 'isDiscountApplied'], true);
          this.setServerCreationState(['pricing', 'discountCode'], data.data.discount.code);
          this.setServerCreationState(['pricing', 'discountedTotal'], data.data.totals);
        }

        if (data.name === 'checkout.discount.removed') {
          this.setServerCreationState(['pricing', 'isDiscountApplied'], false);
          this.$clyNotify({ message: 'Discount removed', sticky: false, type: 'info' });
        }
      },
    });

    // remove legacy intercom cookies if they exist
    if (getCookie('intercom-device-id-svtp1z5n')) {
      deleteCookie('intercom-device-id-svtp1z5n', '/', '.countly.com');
      deleteCookie('intercom-session-svtp1z5n', '/', '.countly.com');
    }

    if (this.isIntercomVisible) {
      await this.initIntercom(this.loggedInUser);
    }

    setInterval(this.check, 60000);
  },
  methods: {
    ...mapActions(useDeploymentStore, ['setConfigurables', 'setPlans']),
    ...mapActions(useAuthStore, ['refreshLoggedInUser']),
    ...mapActions(useServerCreationStore, ['setServerCreationState']),
    onClose() {
      this.isServerCreationPopupOpen = false;
    },
    // Primary Google Ads conversion. Fired from Paddle's checkout.completed
    // callback — the only success-gated signal in the inline checkout flow
    // (there is no post-Paddle redirect page in this app).
    pushGtmSubscriptionComplete(eventData) {
      const serverCreationStore = useServerCreationStore();
      const subscription = serverCreationStore.serverCreationState?.data?.subscription || {};
      const plan = (serverCreationStore.plans?.data || []).find((p) => p._id === subscription.planId);

      pushGtmEvent(GtmEvent.SUBSCRIPTION_COMPLETE, {
        account_id: this.loggedInUser?._id,
        account_email: this.loggedInUser?.email,
        paddle_transaction_id: eventData.data?.transaction_id,
        tier: subscription.planCode,
        is_trial: !!plan?.paddleTrialPeriod,
        value: eventData.data?.totals?.total ?? 0,
        recurring_value: eventData.data?.recurring_totals?.total,
        currency: eventData.data?.currency_code,
      });
    },
    async startIntercom() {
      await this.initIntercom(this.loggedInUser);
    },
    async check() {
      let result;
      if (this.isLoggedIn === true) {
        result = await this.refreshLoggedInUser();
        if (result.isErr()) {
          this.$router.push('/account/signin');
        }
      }
    },
    async getConfigurables(providerCode) {
      const configurablesRes = await DeploymentService.getConfigurables({ providerCode });

      if (configurablesRes.isOk()) {
        configurablesRes.value.payload.sizes = configurablesRes.value.payload.sizes.sort((a, b) => {
          if (a.size.value > b.size.value) {
            return 1;
          }
          if (a.size.value < b.size.value) {
            return -1;
          }
          return 0;
        });
        this.configurables[providerCode] = configurablesRes.value.payload;
      } else {
        this.$clyNotify({ message: 'Deployment configurables could not be loaded', sticky: false, type: 'error' });
      }
    },
    async getPlans() {
      const plansRes = await DeploymentService.getPlans(this.selectedCompany);

      if (plansRes.isOk()) {
        this.plans = plansRes.value.payload;
      } else {
        this.$clyNotify({ message: 'Plans could not be loaded', sticky: false, type: 'error' });
      }
    },
    async loadConfigurables() {
      if (process.env.NODE_ENV === 'test') {
        await this.getConfigurables('mock-cloud');
      } else {
        await this.getConfigurables('gcp');
      }
      await this.getPlans();
      this.setConfigurables(this.configurables);
      this.setPlans(this.plans);
    },
  },
};
</script>

<template>
  <!-- v-loading="loggedInUserState === 'LOADING_AUTO'" -->
  <div
    id="app"
    class="cly-vue-theme-clydef text-cool-gray-100"
  >
    <top-progress-bar />
    <environment-banner />
    <component :is="currentLayout">
      <router-view />
    </component>
    <generic-popups />
    <payment-modal v-if="isLoggedIn && showPaymentModal" />
  </div>
</template>
